import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const serviceCategories = [
  {
    id: 'installation',
    title: 'Installation Services',
    description: 'Professional installation of medical gas pipeline systems with strict adherence to safety standards.',
    features: [
      '24/7 emergency support',
      'Preventive maintenance',
      'System optimization',
      'Performance monitoring'
    ]
  },
  {
    id: 'maintenance',
    title: 'Maintenance & Support',
    description: 'Regular maintenance and emergency support services to ensure system reliability.',
    features: [
      'Scheduled maintenance',
      'Emergency repairs',
      'System upgrades',
      'Performance testing'
    ]
  },
  {
    id: 'consultation',
    title: 'Consultation & Design',
    description: 'Expert consultation and design services for medical gas pipeline systems.',
    features: [
      'System design',
      'Technical specifications',
      'Project planning',
      'Compliance consulting'
    ]
  },
  {
    id: 'testing',
    title: 'Testing & Certification',
    description: 'Comprehensive testing and certification services for medical gas systems.',
    features: [
      'Quality assurance',
      'Safety certification',
      'Performance validation',
      'Compliance verification'
    ]
  }
];

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div>
      <Helmet>
        <title>Hospital Services | MGPS Installation & Turnkey Projects</title>
        <meta name="description" content="Explore our hospital services including Medical Gas Pipeline System (MGPS) installation, modular operation theatre setup, and turnkey hospital projects in India." />
        <meta name="keywords" content="Hospital Services, MGPS Installation, Turnkey Hospital Projects, Medical Gas Pipeline Services, Modular Operation Theatre, Hospital Planning" />
        <link rel="canonical" href="https://www.hospitalbuild.in/services" />
      </Helmet>
      {/* <div className="min-h-screen bg-gray-50">
    <div className="container mx-auto px-4 py-8"> */}
      {/* Hero Section */}
      <div className="flex gap-8 mb-16">
        {/* Hero Section */}
        <div
          className="relative w-1/2 h-[400px] rounded-lg"
          style={{
            backgroundImage: 'url("/images/blog/plant-room.svg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-[#2C2C90]/80 rounded-lg"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
            <h1 className="text-6xl font-bold">Our Services</h1>
          </div>
        </div>

        {/* Welcome Section */}
        <div className="w-1/2 flex flex-col justify-center">
          <h2 className="text-4xl font-bold text-[#2C2C90] mb-6">
            Comprehensive Medical Gas Pipeline Solutions
          </h2>
          <p className="text-gray-700 text-lg">
            Hospital Build provides comprehensive end-to-end solutions for medical gas pipeline systems. Our expert team ensures seamless service, guiding you from initial consultation to order placement. With a strong focus on quality and efficiency, we offer reliable solutions tailored to your needs. Our dedicated sales and customer support team ensures a smooth experience, delivering excellence in every step of the process. Trust us for dependable medical gas pipeline solutions.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {serviceCategories.map((service) => (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${
                  selectedService === service.id ? 'ring-2 ring-[#00B300]' : ''
                }`}
                onClick={() => setSelectedService(service.id === selectedService ? null : service.id)}
              >
                <div className="p-8">
                  {/* Service Icon */}
                  <div className="w-16 h-16 bg-[#2C2C90]/10 rounded-full flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-[#00B300]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {service.id === 'installation' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      )}
                      {service.id === 'maintenance' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      )}
                      {service.id === 'consultation' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      )}
                      {service.id === 'testing' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      )}
                    </svg>
                  </div>

                  {/* Service Content */}
                  <h3 className="text-2xl font-bold mb-4 text-[#2C2C90]">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>

                  {/* Features */}
                  <motion.ul
                    className="space-y-3"
                    initial="hidden"
                    animate={selectedService === service.id ? "visible" : "hidden"}
                    variants={{
                      visible: { height: 'auto', opacity: 1 },
                      hidden: { height: 0, opacity: 0 },
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {service.features.map((feature, index) => (
                      <motion.li
                        key={index}
                        className="flex items-center text-gray-600"
                        variants={itemVariants}
                      >
                        <svg className="w-5 h-5 text-[#00B300] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </motion.li>
                    ))}
                  </motion.ul>

                  {/* CTA Button */}
                  <motion.button
                    className="mt-6 w-full bg-[#2C2C90] text-white py-3 rounded-md hover:bg-[#1A1A5C] transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Schedule Consultation
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-[#2C2C90]">Why Choose Our Services?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              With decades of experience in medical gas pipeline systems, we provide reliable,
              efficient, and compliant solutions for healthcare facilities.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Expert Team',
                description: 'Certified professionals with years of experience',
                icon: (
                  <svg className="w-12 h-12 text-[#00B300]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
              },
              {
                title: 'Quality Assured',
                description: 'HTM 02-01 compliant services and installations',
                icon: (
                  <svg className="w-12 h-12 text-[#00B300]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                ),
              },
              {
                title: '24/7 Support',
                description: 'Round-the-clock emergency assistance',
                icon: (
                  <svg className="w-12 h-12 text-[#00B300]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-8 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-center mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* </div>
    </div> */}
    </div>
  );
};

export default Services; 
